import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLookupQuery } from 'services/auth';

type Props = {
  redirectTo?: string;
  redirectIfFound?: boolean;
};

/**
 * The session, read straight from the cached `/auth/lookup` query: null while the first lookup is
 * in flight, then `{ isLoggedIn }`. With a cached lookup it is known on the first render, so a
 * component that mounts on navigation doesn't flash a spinner.
 */
const useUser = ({ redirectTo, redirectIfFound = false }: Props = {}) => {
  const { isError, isSuccess, isFetching } = useLookupQuery('');
  const navigate = useNavigate();

  // RTK Query keeps isSuccess during a refetch that follows a success
  const isLoggedIn = isSuccess ? true : isError && !isFetching ? false : null;
  const user = useMemo(() => (isLoggedIn == null ? null : { isLoggedIn }), [isLoggedIn]);

  useEffect(() => {
    if (isFetching || !redirectTo) {
      return;
    }

    if (isSuccess && redirectIfFound) {
      navigate(redirectTo);
    } else if (isError && !redirectIfFound) {
      navigate(redirectTo);
    }
  }, [navigate, redirectTo, redirectIfFound, isSuccess, isError, isFetching]);

  return user;
};

export default useUser;
